import * as portalApi from '../api/portal-api';
import { selectCompanyId } from '../redux/modules/company';
import { selectUserId } from '../redux/modules/user';

import { queryClient } from './queryClientUtil';

/**
 * Whether archiving or deleting something should warn that Procore will change.
 *
 * Companies can switch on Procore insurance writing, after which PINS keeps a
 * "PINS Record" insurance row per vendor per Procore project. Archiving or
 * deleting a record, project, department or insured removes those rows, which
 * can change whether Procore Pay holds a vendor's payment.
 *
 * Everything here fails safe: when the answer cannot be had, the warning shows.
 * An unneeded warning costs a click; a missing one lets a payment-affecting
 * action through unannounced.
 */

export const procoreWritebackKeys = {
  company: companyId => ['procoreWriteback', 'company', companyId],
  department: departmentId => ['procoreWriteback', 'department', departmentId]
};

const STALE_TIME = 60 * 1000;

const UNKNOWN = { enabled: true, determined: false };

/**
 * The company's write state, cached briefly so a run of confirmations does not
 * call the API each time. Never rejects.
 */
export const fetchProcoreWriteback = async companyId => {
  if (!companyId) return UNKNOWN;

  try {
    const data = await queryClient.fetchQuery(
      procoreWritebackKeys.company(companyId),
      () => portalApi.getProcoreWriteback(companyId),
      { staleTime: STALE_TIME, retry: false }
    );

    return { enabled: data?.enabled !== false, determined: !!data?.determined };
  } catch (error) {
    return UNKNOWN;
  }
};

const toDepartmentStatus = data => ({
  enabled: data?.enabled !== false,
  determined: !!data?.determined,
  affectedRecords: Number.isInteger(data?.affected_records) ? data.affected_records : null
});

export const fetchDepartmentProcoreWriteback = async departmentId => {
  try {
    // Not served from cache: the count changes when projects are transferred
    // out, which is the step right before a department is deleted.
    const data = await queryClient.fetchQuery(
      procoreWritebackKeys.department(departmentId),
      () => portalApi.getDepartmentProcoreWriteback(departmentId),
      { staleTime: 0, retry: false }
    );

    return toDepartmentStatus(data);
  } catch (error) {
    return { ...UNKNOWN, affectedRecords: null };
  }
};

export const departmentProcoreWritebackQuery = departmentId => ({
  queryKey: procoreWritebackKeys.department(departmentId),
  queryFn: () => portalApi.getDepartmentProcoreWriteback(departmentId),
  select: toDepartmentStatus,
  enabled: !!departmentId,
  retry: false,
  refetchOnMount: 'always'
});

/**
 * Thunk: the write state for the current company, plus who is asking.
 * Dispatching it returns a promise, so plain menu handlers can await it.
 */
export const loadProcoreWarningContext = () => async (dispatch, getState) => {
  const state = getState();
  const companyId = selectCompanyId(state);
  const userId = selectUserId(state);
  const status = await fetchProcoreWriteback(companyId);

  return { ...status, companyId, userId };
};

/* "Don't warn me again" for the record-level warning only.
 *
 * Per user and per company, so one person's choice never silences the warning
 * for a colleague -- including on a shared machine, which a plain cookie would
 * not guarantee. Kept in this browser: on another device the warning returns,
 * which is the safe direction to be wrong in. Never applied to project,
 * department or insured warnings, which affect many vendors at once.
 */
const dismissalKey = ({ userId, companyId }) => `pins.procoreRecordWarningDismissed.${userId}.${companyId}`;

export const isRecordWarningDismissed = context => {
  if (!context?.userId || !context?.companyId) return false;

  try {
    return window.localStorage.getItem(dismissalKey(context)) === 'true';
  } catch (error) {
    return false;
  }
};

export const dismissRecordWarning = context => {
  if (!context?.userId || !context?.companyId) return;

  try {
    window.localStorage.setItem(dismissalKey(context), 'true');
  } catch (error) {
    // Storage blocked: the warning simply keeps showing.
  }
};

/**
 * Whether a confirmation for this scope should carry the Procore warning.
 */
export const shouldWarnAboutProcore = (context, scope) => {
  if (!context?.enabled) return false;

  return !(scope === 'record' && isRecordWarningDismissed(context));
};
